import React, {useEffect, useState} from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';

import { overestimationShema, overestimationBaseShema } from './overestimationShema';
import { HInput, HTextarea } from '../../../../validation/HForm';
import { ActionButton } from '../../../common';
import { listRoles, viewDocument } from '../../../../helpers/data-constants';
import Loader from './Loader/Loader';

import './overestimation.scss';
import moment from "moment";
import DeleteIcon from "../../../../assets/icons/trash.svg";
import {useSelector} from "react-redux";
import {getUserFromLocalStorage} from "../../../../helpers/common";
import { handleKeyPressTA } from '../../../../helpers/common';

const OverestimationLayout = ({
                                handleCloseModal,
                                data,
                                submitOverestimation,
                                view,
                                isModalLoading,
                                garage,
                                isOverestimation,
                                tab
                              }) => {
  const notEmpty = data && data.length !== 0;
  const shema = view ? overestimationBaseShema : overestimationShema;
  const userInfo = getUserFromLocalStorage();
  const goodSpecialist = (userInfo.roles.length === 1 && userInfo.roles.includes(listRoles.GOODS_SPECIALIST))
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      dealsIds: [
          {
              revaluationDays: 0,
              totalToPay: 0,
              dealId: '',
              estimatedValue: '',
              totalEstimatedValue: '',
          },
      ],
    },
    resolver: joiResolver(shema),
  });
  const { append, remove, ...fieldArray } = useFieldArray({
    control: form.control,
    name: 'dealsIds',
    keyName: 'dealId',
  });

  const [formValues, setFormValues] = useState(form.getValues());
  const [disabled, setDisabled] = useState(false);
  const { accessFeatures } = useSelector(({ user }) => user);
  const daysUntilRepricing = 10;
  const hasErrors = Object.keys(form.formState.errors).length > 0 ||
    fieldArray.fields.some(item => formValues[`dealsIds[${item.dealId}].isBlockingError`]);

    useEffect(() => {
        if (fieldArray.fields.length === 0) {
            handleCloseModal();
        }
    }, [fieldArray.fields])

  useEffect(() => {
    if (!isModalLoading) {
        remove(0);
      append(
          data.map((i) => {
            if (!garage) {
              let errorMessage = ''
              if (i.revaluationDays < daysUntilRepricing) {
                const now = moment();
                const daysAGo = now.subtract(i.revaluationDays, 'days')
                const repricingAvailabilityDate = daysAGo.add(daysUntilRepricing, 'days').format('DD.MM.YYYY');
                errorMessage = `Ближайшая уценка будет доступна ${repricingAvailabilityDate}`
                setFormValues(prevState => ({
                  ...prevState,
                  [`dealsIds[${i.id}].errorMessage`]: errorMessage,
                  [`dealsIds[${i.id}].style`]: 'input inputNotificationError',
                }))
              }
            }
            return {
              dealId: i.id,
              estimatedValue: i.totalEstimatedValue.toFixed(2),
              totalEstimatedValue: i.totalEstimatedValue.toFixed(2),
              revaluationDays: i.revaluationDays
            }}),
      );
    }
  }, [isModalLoading, append, remove, data]);

  useEffect(() => {
    if (notEmpty && !garage && accessFeatures['expired.thing.estimation']) {
      const isRevaluation = fieldArray.fields.some(el => el.revaluationDays < daysUntilRepricing);
      setDisabled(isRevaluation)
    }
  }, [fieldArray.fields, notEmpty, garage, setDisabled]);

  const handleChange = (event, id, totalEstimatedValue, totalToPay) => {
    const isCashboxEnabled = accessFeatures['cashboxNameTypeOne'];
    const sum = (totalEstimatedValue / 100 * 80).toFixed(2)
    const value = event.target.value;
    const index = fieldArray.fields.findIndex(el => el.dealId === id);
    const element = fieldArray.fields[index];
    element.estimatedValue = value;

    let errorMessage = '';
    let isBlockingError = false;

    if (value === '') {
      errorMessage = '';
    }
    else if (+value === 0 || +value > 99999999.99) {
      errorMessage = ''
    }
    else if (!isCashboxEnabled && goodSpecialist) {
      const validationResult  = validateEstimatedValue(+value, sum, +totalEstimatedValue, +totalToPay)
      errorMessage = validationResult?.message;
      isBlockingError = validationResult?.isError || false;
    }
    else if (+value > +totalToPay) {
      errorMessage = 'Сумма оценки не может быть больше суммы на текущий день!';
      isBlockingError = false;
    } 
    else if (+value < sum) {
      errorMessage = `Максимальная уценка доступна до ${sum}.руб`;
      isBlockingError = false;
    }

    element.showError = errorMessage !== '';
    setFormValues({
      ...formValues,
      [`dealsIds[${id}].estimatedValue`]: value,
      [`dealsIds[${id}].errorMessage`]: errorMessage,
      [`dealsIds[${id}].isBlockingError`]: isBlockingError,
      [`dealsIds[${index}].style`]: errorMessage ? 'input inputNotificationError' : 'input',
    });
    form.setValue(`dealsIds[${index}].estimatedValue`, value);
  };

  const validateEstimatedValue = (value, sum, totalEstimatedValue, totalToPay) => {
    if (tab === 'STORE') {
        if (value > totalToPay) {
          return { 
            message: 'Переоценивать вещи выше суммы задолженности на момент перевода в магазин запрещено',
            isError: true
        };
        }
        if (value < sum) {
            return {message: `Максимальная уценка доступна до ${sum}.руб`,
            isError: false
          };
        }
    }

    if (tab === 'FOR_TRANSMISSION') {
        if (value < totalEstimatedValue) {
          return {
            message: 'Сумма не может быть ниже суммы первоначальной оценки',
            isError: true
        };
        }
        if (value > totalToPay) {
          return {
            message: 'Сумма не может быть выше суммы на текущий день',
            isError: true
          };
        }
    }
};

  const removeItemFromData = (id) => {
    const fieldIndex = fieldArray.fields.findIndex(el => String(el.dealId) === String(id));
    remove(fieldIndex);

    setFormValues(prevState => {
      const updatedFormValues = {...prevState};
      delete updatedFormValues[`dealsIds[${fieldIndex}].errorMessage`];
      return updatedFormValues;
    });
}

  const getDefaultValue = (estimatedValue, totalToPay) => {
    if (!accessFeatures['expired.thing.estimation']) {
      return estimatedValue;
    }
    return garage ? estimatedValue : (isOverestimation ? totalToPay?.toFixed(2) : '');
  };

  const getPlaceholder = (estimatedValue, totalToPay) => {
    if (!accessFeatures['expired.thing.estimation']) {
      return estimatedValue;
    }
    return isOverestimation ? totalToPay?.toFixed(2) : estimatedValue;
  };

  const returnTitle = () => {
    switch (tab) {
      case 'STORE': {
        return 'Приказ № 279 от  16.12.2024'
      }
      case 'FOR_TRANSMISSION': {
        return 'Приказ № 201 от  07.10.2024'
      }
      default:
        return null
    }
  }

  const addReason = () => form.setValue('base', returnTitle());

  return (
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(submitOverestimation)}
          onKeyDown={handleKeyPressTA}>
          <div className="modal-header">
            <h2>Переоценка</h2>
          </div>
          <div className="modal-body">
            <div className="form-element overestimation-form-element">
              <label className="form-element-label" htmlFor="documentNumber">
                Переоценка №
              </label>
              <div className="form-element-data combobox-lg">
                <HInput h="true" name="documentNumber" className="input" type="text" decimals={0} />
              </div>
            </div>
            {isModalLoading ? (
                <Loader />
            ) : (
                <table className="table hover overestimation-table">
                  <thead>
                  <tr>
                    <th scope="col">
                      <p>Договор</p>
                    </th>
                    <th scope="col">
                      <p>Наименование</p>
                    </th>
                    <th scope="col">
                      <p>Количество</p>
                    </th>
                    <th scope="col">
                      <p>
                        Сумма на текущий <br /> день, руб.
                      </p>
                    </th>
                    <th scope="col">
                      <p>
                        Оценочная <br />
                        стоимость, руб.
                      </p>
                    </th>
                    <th scope="col">
                      <p>Сумма оценки, руб.</p>
                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  { notEmpty &&
                      fieldArray.fields.map((i, idx) => {
                        const fieldPosition = data.find(el => String(el.id) === String(i.dealId));
                        const revaluationDays = i.revaluationDays < daysUntilRepricing
                        return (
                            <tr key={i.dealId}>
                              <td>
                                <p>{fieldPosition?.dealNumber || fieldPosition?.id}</p>
                                <HInput
                                    type="hidden"
                                    name={`dealsIds[${idx}].dealId`}
                                    defaultValue={i.dealId}
                                />
                              </td>
                              <td>
                                <p>{fieldPosition?.positions[0].names.join(', ')}</p>
                                {
                                    fieldPosition?.type === "CARS" && (
                                        <>
                                          <p>{fieldPosition?.positions[0].model}</p>
                                          <p>{fieldPosition?.positions[0].registrationNumber}</p>
                                          <p>{fieldPosition?.positions[0].vin
                                          }</p>
                                        </>
                                    )
                                }
                              </td>
                              <td>
                                <p>{fieldPosition?.positions[0].quantity}</p>
                              </td>
                              <td>
                                <p>{fieldPosition?.totalToPay.toFixed(2)}</p>
                              </td>
                              <td>
                                <p>{i.totalEstimatedValue}</p>
                                <HInput
                                    type="hidden"
                                    name={`dealsIds[${idx}].totalEstimatedValue`}
                                    defaultValue={i.totalEstimatedValue}
                                />
                              </td>
                              <td>
                                <div className="form-element-data number">
                                  <HInput
                                      name={`dealsIds[${idx}].estimatedValue`}
                                      defaultValue={getDefaultValue(i.estimatedValue, fieldPosition?.totalToPay)}
                                      className={formValues[`dealsIds[${i.dealId}].style`] || 'input'}
                                      type="text"
                                      decimals={2}
                                      onChange={!garage ? (e) => handleChange(e, i.dealId, i.totalEstimatedValue, fieldPosition.totalToPay, i.revaluationDays) : null}
                                      readOnly={!garage ? revaluationDays : false}
                                      placeholder={getPlaceholder(i.estimatedValue, fieldPosition?.totalToPay)}
                                  />
                                  {!garage && <span className={revaluationDays ? "overestimation-textNotificationError" : 'overestimation-textNotification'}>
                                  {formValues[`dealsIds[${i.dealId}].errorMessage`]}
                                </span>}
                                </div>
                              </td>
                              <td className='overestimation-deleteIcon'>
                                   {
                                     !garage && <DeleteIcon
                                           onClick={() => removeItemFromData(i.dealId, idx)}
                                           className='overestimation-deleteIcon'

                                       />
                                   }
                              </td>
                            </tr>
                        );
                      })}
                  </tbody>
                </table>
            )}
            {view && (
                <div className="form-element overestimation-base">
                  <label className="form-element-label" htmlFor="base">
                    Основание
                  </label>
                  <div className="form-element-data overestimation-base">
                    <HTextarea h="true" name="base" className="input"/>
                    {
                        !accessFeatures["cashboxNameTypeOne"] && !garage && <p className="toFormRegisterModal-reason">
                          Например: <span
                            onClick={addReason}>{returnTitle()}</span>
                        </p>
                    }
                  </div>
                </div>
            )}
          </div>
          <div className="modal-footer overestimation-modal-footer">
            <ActionButton text="Сохранить" styleName={disabled || hasErrors ? "button-light" : "button"} submit disabled={disabled || hasErrors}/>
            <ActionButton
                text="Отменить"
                func={handleCloseModal}
                param={viewDocument.isOverestimation}
                styleName="button-light"
            />
          </div>
        </form>
      </FormProvider>
  );
};

export default OverestimationLayout;
